import { type ComponentProps, type FC, useEffect, useState } from "react";
import { SearchInput } from "@hightouch/htui";
import { useDebouncedCallback } from "use-debounce";

type Props = {
  /**
   * Seed value for the input. Subsequent changes to `initialValue` do NOT
   * resync local state; the component owns its state after mount.
   */
  initialValue: string;
  /** Called on mount, after typing settles, and on unmount if input is pending.
   * Its identity may change between renders; every notification goes through
   * the debounced callback, so the mount one still fires exactly once. */
  onDebouncedChange: (value: string) => void;
  /**
   * Optional: fires when the debounce pending state changes. Useful for
   * driving a table loading indicator while the debounce settles. The mount
   * notification reports `false`, as does flushing pending input on unmount.
   */
  onPendingChange?: (pending: boolean) => void;
  debounceMs?: number;
} & Omit<ComponentProps<typeof SearchInput>, "value" | "onChange">;

export const DebouncedSearchInput: FC<Props> = ({
  initialValue,
  onDebouncedChange,
  onPendingChange,
  debounceMs = 300,
  ...searchInputProps
}) => {
  // Preserve the mount notification even if the parent later supplies another seed.
  const [seed] = useState(initialValue);
  const [value, setValue] = useState(initialValue);
  const commitSearch = useDebouncedCallback((next: string) => {
    onDebouncedChange(next);
    onPendingChange?.(false);
  }, debounceMs);

  // Routing the seed through `commitSearch` keeps this effect off the caller's
  // callback identity, which changes on every parent render when the handler is
  // declared in a render body. `commitSearch` is stable, so the seed fires once.
  useEffect(() => {
    commitSearch(seed);
    commitSearch.flush();
  }, [commitSearch, seed]);

  // Closing a sidebar must commit its pending search before the input disappears.
  useEffect(
    () => () => {
      commitSearch.flush();
    },
    [commitSearch],
  );

  return (
    <SearchInput
      value={value}
      onChange={(e) => {
        const next = e.target.value;
        setValue(next);
        onPendingChange?.(true);
        commitSearch(next);
      }}
      {...searchInputProps}
    />
  );
};
